import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type AuditLog = components["schemas"]["InvoiceAuditLogResponse"];

export function InvoiceAuditLogTable({ invoiceId }: { invoiceId?: string }) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;

    async function fetchLogs() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET(
        "/admin/invoices/{invoiceId}/audit-logs",
        {
          params: {
            path: { invoiceId: invoiceId! },
            query: { Cursor: pagination.cursor, Limit: pagination.limit },
          },
        },
      );
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load audit log.");
      } else {
        setLogs(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchLogs();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Transition</TableHead>
              <TableHead>Actor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground h-24 text-center">
                  No audit log entries.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDateTime(log.createdAt)}
                  </TableCell>
                  <TableCell className="font-medium">{log.action}</TableCell>
                  <TableCell>
                    {!log.fromState && !log.toState ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className="text-sm">
                        {log.fromState ? (
                          <Badge variant="outline">{log.fromState}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        <span className="mx-1.5 text-muted-foreground">→</span>
                        {log.toState ? (
                          <Badge variant="secondary">{log.toState}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.actorType === "System" || !log.actorId ? (
                      <Badge variant="outline">System</Badge>
                    ) : (
                      <Link to={`/users/${log.actorId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {log.actorId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        limitOptions={[20, 50, 100]}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
